import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Vote, Check, Send, Clock, Lightbulb, Heart, Pencil, ArrowRight, Share2, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { resolvePollIcon } from '../lib/pollIcons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Countdown-Hook: verbleibende Zeit bis `ends_at` (ISO).
const useCountdown = (endsAt) => {
  const [tick, setTick] = useState(Date.now());
  useEffect(() => {
    if (!endsAt) return undefined;
    const id = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [endsAt]);
  if (!endsAt) return null;
  try {
    const end = new Date(endsAt).getTime();
    const total = Math.max(0, end - tick);
    const days = Math.floor(total / 86400000);
    const h = Math.floor((total % 86400000) / 3600000);
    const m = Math.floor((total % 3600000) / 60000);
    const s = Math.floor((total % 60000) / 1000);
    return { days, h, m, s, total, ended: total === 0 };
  } catch {
    return null;
  }
};

// Sparkle-Deko im Hintergrund (leichte SVG-Punkte, rein dekorativ).
const SparklesBg = () => (
  <svg
    aria-hidden
    className="pointer-events-none absolute inset-0 h-full w-full opacity-40"
    viewBox="0 0 800 500"
    preserveAspectRatio="none"
  >
    {[
      [60, 40], [720, 90], [140, 220], [660, 260], [80, 380],
      [780, 420], [400, 40], [520, 470], [220, 460], [600, 200],
    ].map(([x, y], i) => (
      <g key={i} transform={`translate(${x},${y})`}>
        <circle r="2" fill="#38bdf8" opacity="0.55" />
        <circle r="6" fill="none" stroke="#38bdf8" strokeWidth="0.5" opacity="0.3" />
      </g>
    ))}
  </svg>
);

// Kleiner Ballon unten links (SVG-Deko, keine externen Bilder).
const Balloon = () => (
  <svg
    aria-hidden
    className="pointer-events-none absolute -bottom-2 left-4 md:left-8 w-16 md:w-24 opacity-90"
    viewBox="0 0 80 130"
  >
    <ellipse cx="40" cy="42" rx="30" ry="36" fill="#2563eb" />
    <ellipse cx="30" cy="30" rx="7" ry="10" fill="#93c5fd" opacity="0.75" />
    <path d="M40 78 L36 86 L44 86 Z" fill="#1d4ed8" />
    <path d="M40 86 C 45 100, 34 108, 40 128" stroke="#1d4ed8" strokeWidth="1.5" fill="none" />
  </svg>
);

// Sprechblase mit „Deine Idee für unser nächstes Highlight!"
const SpeechBubble = ({ text }) => (
  <div className="relative select-none" aria-hidden={false}>
    <div className="relative bg-white border-2 border-sky-200 rounded-2xl px-4 py-2.5 text-sky-800 font-semibold text-xs md:text-sm shadow-sm max-w-[180px] leading-snug">
      <span className="flex items-start gap-1.5">
        <Lightbulb className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />
        <span>{text}</span>
      </span>
      {/* Blase-Pfeil */}
      <span className="absolute -bottom-2 left-6 w-3 h-3 bg-white border-b-2 border-r-2 border-sky-200 rotate-45" />
    </div>
    {/* Sparkles um die Blase */}
    <svg aria-hidden className="absolute -top-3 -right-2 w-6 h-6 text-sky-300" viewBox="0 0 24 24" fill="none">
      <path d="M12 2v6M12 16v6M2 12h6M16 12h6M5 5l3 3M16 16l3 3M5 19l3-3M16 8l3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  </div>
);

// Default-Maskottchen (inline SVG). Wird nur benutzt wenn kein mascot_url gesetzt ist.
const DefaultMascot = () => (
  <svg
    aria-hidden
    className="pointer-events-none absolute bottom-0 right-2 md:right-4 w-24 md:w-40 lg:w-48"
    viewBox="0 0 180 220"
  >
    {/* Beine */}
    <rect x="60" y="185" width="18" height="20" rx="4" fill="#f59e0b" />
    <rect x="102" y="185" width="18" height="20" rx="4" fill="#f59e0b" />
    {/* Körper (Hüpfburg) */}
    <path d="M30 90 Q30 55 65 55 Q75 40 90 40 Q105 40 115 55 Q150 55 150 90 L150 190 Q90 200 30 190 Z" fill="#2563eb" />
    {/* Türe */}
    <path d="M78 130 Q78 110 90 110 Q102 110 102 130 L102 165 L78 165 Z" fill="#fbbf24" />
    {/* Fenster mit Text */}
    <rect x="45" y="115" width="24" height="50" rx="4" fill="#fef3c7" />
    <rect x="111" y="115" width="24" height="50" rx="4" fill="#fef3c7" />
    <text x="90" y="80" textAnchor="middle" fontSize="8" fontWeight="800" fill="#fef3c7" fontFamily="system-ui">HÜPFBURGEN</text>
    <text x="90" y="92" textAnchor="middle" fontSize="10" fontWeight="900" fill="#fbbf24" fontFamily="system-ui">VERLEIH</text>
    {/* Gesicht */}
    <circle cx="72" cy="65" r="3" fill="#0f172a" />
    <circle cx="108" cy="65" r="3" fill="#0f172a" />
    <path d="M75 78 Q90 88 105 78" stroke="#0f172a" strokeWidth="2.5" fill="none" strokeLinecap="round" />
    {/* Bauhelm oben */}
    <path d="M65 42 Q90 20 115 42 L115 48 L65 48 Z" fill="#fbbf24" />
    <rect x="60" y="46" width="60" height="4" rx="2" fill="#f59e0b" />
    {/* Arm mit Daumen hoch */}
    <path d="M148 120 Q170 118 170 100 Q170 90 158 92 Q160 82 152 82 Q145 82 145 92 Q140 95 140 105 Z" fill="#2563eb" />
    <circle cx="163" cy="92" r="4" fill="#fbbf24" />
  </svg>
);

const PollSection = ({ compact = false, variant = 'classic' }) => {
  const [poll, setPoll] = useState(null);
  const [picks, setPicks] = useState(new Set());
  const [voting, setVoting] = useState(false);
  const [voted, setVoted] = useState(false);
  const [suggestion, setSuggestion] = useState('');
  const [suggestionSent, setSuggestionSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/polls/active`);
        setPoll(res.data);
        if (res.data?.id && localStorage.getItem(`voted_${res.data.id}`)) setVoted(true);
        if (res.data?.id && localStorage.getItem(`suggested_${res.data.id}`)) setSuggestionSent(true);
      } catch { /* ignore */ }
    })();
  }, []);

  const cd = useCountdown(poll?.ends_at);
  const ended = !!(cd && cd.ended);

  // Alle Texte per CMS überschreibbar — hier zentrale Defaults.
  const T = useMemo(() => ({
    eyebrow: poll?.eyebrow || 'Umfrage',
    headline: poll?.headline || 'Eure Meinung ist gefragt!',
    subtitle: poll?.subtitle || 'Deine Idee hilft uns, unser Angebot noch besser für euch zu machen!',
    submitLabel: poll?.submit_label || 'Idee absenden',
    thankVote: poll?.thank_you_vote || 'Danke für deine Stimme!',
    suggestPlaceholder: poll?.suggest_placeholder || 'Dein Themen-Vorschlag…',
    thankSuggest: poll?.thank_you_suggest || 'Danke für deinen Themen-Vorschlag!',
    endedLabel: poll?.ended_label || 'Umfrage beendet',
    sectionLabel: poll?.section_label || 'Welche Themen interessieren dich?',
    sectionHint: poll?.section_hint || 'Wähle ein oder mehrere Themen aus:',
    customHeading: poll?.custom_heading || 'Deine eigene Idee',
    customHint: poll?.custom_hint || 'Du hast ein anderes Thema im Kopf? Schreib es uns!',
    footerThanks: poll?.footer_thanks || 'Danke für deine Unterstützung!',
    bubbleText: poll?.bubble_text || 'Deine Idee für unser nächstes Highlight!',
  }), [poll]);

  if (!poll) return null;

  const options = poll.options || [];
  const hasOptions = options.length > 0;
  const canVote = hasOptions && !voted && !ended;
  const canSuggest = poll.allow_custom !== false && !ended && !suggestionSent;

  const toggle = (id) => {
    if (!canVote) return;
    const s = new Set(picks);
    if (s.has(id)) s.delete(id); else s.add(id);
    setPicks(s);
  };

  // Ein Klick auf „Idee absenden" schickt sowohl die Vote (falls Optionen gewählt)
  // als auch den Themen-Vorschlag (falls Text eingegeben) — damit sich die Aktion
  // wie im Screenshot anfühlt: ein Button für alles.
  const submitAll = async () => {
    if (submitting) return;
    const wantVote = canVote && picks.size > 0;
    const wantSuggest = canSuggest && suggestion.trim().length > 0;
    if (!wantVote && !wantSuggest) {
      toast.error('Bitte wähle ein Thema oder schreib deine Idee.');
      return;
    }
    setSubmitting(true);
    let anyOk = false;
    if (wantVote) {
      setVoting(true);
      try {
        const ids = Array.from(picks).join(',');
        await axios.post(`${API}/polls/${poll.id}/vote?option_ids=${ids}`);
        anyOk = true;
        setVoted(true);
        localStorage.setItem(`voted_${poll.id}`, '1');
      } catch { /* swallow */ }
      setVoting(false);
    }
    if (wantSuggest) {
      try {
        await axios.post(`${API}/polls/${poll.id}/suggest?text=${encodeURIComponent(suggestion.trim())}`);
        anyOk = true;
        setSuggestion('');
        setSuggestionSent(true);
        localStorage.setItem(`suggested_${poll.id}`, '1');
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Fehler beim Senden');
      }
    }
    if (anyOk) toast.success('Danke für deinen Beitrag!');
    setSubmitting(false);
  };

  const cdChip = cd && !ended ? (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-2 py-0.5 text-[11px] font-semibold"
      data-testid="poll-countdown"
    >
      <Clock className="h-3 w-3" />
      {cd.days > 0
        ? `Noch ${cd.days} Tag${cd.days === 1 ? '' : 'e'}`
        : `Noch ${String(cd.h).padStart(2, '0')}:${String(cd.m).padStart(2, '0')}:${String(cd.s).padStart(2, '0')}`}
    </span>
  ) : null;

  const showMascot = poll.show_mascot !== false;
  const showBalloon = poll.show_balloon !== false;
  const showBubble = poll.show_bubble !== false;
  const showSparkles = poll.show_sparkles !== false;

  // ============================================================
  // Compact-Mode: kleine Teaser-Karte für die Startseite,
  // klickt auf die dedizierte /umfrage-Seite.
  // Zwei Varianten: 'classic' (Standard-Karte) und 'creative'
  // (großer Split-Layout mit gekippten Foto-Karten).
  // ============================================================
  if (compact) {
    const previewOptions = options.slice(0, 6);
    const totalCount = options.length;

    if (variant === 'creative') {
      return (
        <PollCompactCreative
          poll={poll}
          T={T}
          options={options}
          cdChip={cdChip}
        />
      );
    }

    return (
      <section className="py-6 md:py-8" data-testid="poll-section-compact">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/umfrage"
            className="group block relative rounded-3xl border border-sky-100 bg-gradient-to-r from-sky-50/70 via-white to-white shadow-[0_10px_35px_-20px_rgba(56,189,248,0.5)] hover:shadow-[0_15px_45px_-15px_rgba(56,189,248,0.6)] transition-all overflow-hidden"
            data-testid="poll-compact-link"
          >
            {showSparkles && <SparklesBg />}
            <div className="relative flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6 px-5 md:px-8 py-5 md:py-6">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-sky-700 flex-wrap">
                  <Vote className="h-4 w-4" />
                  <span className="text-[11px] font-bold uppercase tracking-widest">{T.eyebrow}</span>
                  {cdChip}
                </div>
                <h2 className="mt-1 text-xl md:text-2xl lg:text-3xl font-black text-sky-800 leading-tight">
                  {T.headline}
                </h2>
                <p className="mt-1 text-sm md:text-base text-sky-900/75 line-clamp-2">
                  {poll.question}
                </p>
                {previewOptions.length > 0 && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {previewOptions.map((o) => {
                      const Icon = resolvePollIcon(o.icon, o.label);
                      const imgSrc = o.image_url
                        ? (o.image_url.startsWith('/api/') ? `${BACKEND_URL}${o.image_url}` : o.image_url)
                        : null;
                      return (
                        <span
                          key={o.id}
                          className="inline-flex items-center gap-1.5 rounded-full bg-white border border-sky-200 text-sky-800 pl-1 pr-2.5 py-0.5 text-xs font-semibold shadow-sm"
                        >
                          {imgSrc ? (
                            <img src={imgSrc} alt="" className="h-5 w-5 rounded-full object-cover" />
                          ) : (
                            <Icon className="h-3.5 w-3.5 ml-1" />
                          )}
                          {o.label}
                        </span>
                      );
                    })}
                    {totalCount > previewOptions.length && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 text-sky-700 px-2.5 py-1 text-xs font-semibold">
                        +{totalCount - previewOptions.length} weitere
                      </span>
                    )}
                  </div>
                )}
              </div>
              <span className="shrink-0 inline-flex items-center gap-2 rounded-full bg-sky-600 group-hover:bg-sky-700 text-white text-sm font-bold px-5 py-2.5 shadow-md transition-colors">
                Jetzt abstimmen
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            </div>
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 md:py-12" data-testid="poll-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative bg-white border border-sky-100 rounded-3xl shadow-[0_10px_40px_-15px_rgba(56,189,248,0.35)] overflow-hidden">
          {/* Hintergrund-Layer */}
          <div className="absolute inset-0 bg-gradient-to-br from-sky-50/60 via-white to-white pointer-events-none" />
          {showSparkles && <SparklesBg />}

          <div className={`relative px-5 md:px-10 pt-6 md:pt-8 ${showMascot ? 'pb-40 md:pb-44 lg:pb-52' : 'pb-8'}`}>
            {/* Sprechblase absolut oben rechts (floating, wie im Design) */}
            {showBubble && (
              <div className="hidden md:block absolute top-6 right-6 lg:right-10 z-10 max-w-[210px]">
                <SpeechBubble text={T.bubbleText} />
              </div>
            )}

            {/* Header */}
            <div className="min-w-0 md:pr-56">
              <div className="flex items-center gap-2 text-sky-700 flex-wrap">
                <Vote className="h-4 w-4" />
                <span className="text-[11px] font-bold uppercase tracking-widest">{T.eyebrow}</span>
                {cdChip}
              </div>
              <h2
                className="mt-1 text-3xl md:text-4xl lg:text-5xl font-black text-sky-800 leading-tight"
                data-testid="poll-headline"
              >
                {T.headline}
              </h2>
              <p className="mt-2 text-base md:text-lg text-sky-900/80 font-medium max-w-2xl">
                {poll.question}
              </p>
              {T.subtitle && (
                <p className="mt-1 text-sm text-neutral-500 max-w-2xl">
                  {T.subtitle}
                </p>
              )}
            </div>
            {/* Kleine Sprechblase für Mobile — unterhalb der Headline */}
            {showBubble && (
              <div className="md:hidden mt-3">
                <SpeechBubble text={T.bubbleText} />
              </div>
            )}

            {ended && (
              <p className="mt-4 text-sm text-neutral-500 font-medium" data-testid="poll-ended-label">
                🔒 {T.endedLabel}
              </p>
            )}

            {/* Options Grid */}
            {hasOptions && !ended && (
              <div className="mt-6">
                <p className="text-base md:text-lg font-bold text-sky-900" data-testid="poll-section-label">
                  {T.sectionLabel}
                </p>
                <p className="text-xs md:text-sm text-neutral-500 mt-0.5">{T.sectionHint}</p>

                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {options.map((o) => {
                    const active = picks.has(o.id);
                    const Icon = resolvePollIcon(o.icon, o.label);
                    const imgSrc = o.image_url
                      ? (o.image_url.startsWith('/api/') ? `${BACKEND_URL}${o.image_url}` : o.image_url)
                      : null;
                    return (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => toggle(o.id)}
                        disabled={voted}
                        className={`group relative rounded-2xl overflow-hidden p-0 text-center transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-sky-400 disabled:opacity-60 disabled:cursor-not-allowed ring-1 ${
                          active
                            ? 'ring-sky-500 shadow-md ring-2 bg-sky-50/60'
                            : 'ring-neutral-200 hover:ring-sky-300 bg-white'
                        }`}
                        data-testid={`poll-vote-${o.id}`}
                        aria-pressed={active}
                      >
                        {/* Visual — nutzt aspect-ratio, füllt komplett bis zur Rundung. */}
                        <div className="relative w-full aspect-[4/3] bg-sky-100">
                          {imgSrc ? (
                            <img
                              src={imgSrc}
                              alt={o.label}
                              className="absolute inset-0 w-full h-full max-w-none object-cover block transition-transform duration-300 group-hover:scale-[1.04]"
                              loading="lazy"
                              draggable="false"
                              style={{ margin: 0, padding: 0 }}
                            />
                          ) : (
                            <span className="absolute inset-0 flex items-center justify-center">
                              <span className="flex h-14 w-14 md:h-16 md:w-16 items-center justify-center rounded-full bg-sky-100 text-sky-700">
                                <Icon className="h-7 w-7 md:h-8 md:w-8" />
                              </span>
                            </span>
                          )}
                        </div>
                        <span className="block px-3 py-3 text-xs md:text-sm font-semibold text-sky-900 leading-tight">
                          {o.label}
                        </span>
                        {/* Checkbox oben rechts */}
                        <span
                          className={`absolute top-2 right-2 z-10 h-5 w-5 rounded-[5px] border-2 flex items-center justify-center transition shadow-sm ${
                            active
                              ? 'bg-sky-600 border-sky-600 text-white'
                              : 'bg-white/90 border-white group-hover:border-sky-400'
                          }`}
                          aria-hidden
                        >
                          {active && <Check className="h-3 w-3" strokeWidth={3.5} />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {hasOptions && voted && !ended && (
              <p
                className="mt-4 text-sm text-green-700 flex items-center gap-1.5 font-medium"
                data-testid="poll-thankyou-vote"
              >
                <Check className="h-4 w-4" /> {T.thankVote}
              </p>
            )}

            {/* Eigene Idee */}
            {poll.allow_custom !== false && !ended && (
              <div className="mt-8">
                <p className="text-base md:text-lg font-bold text-sky-900" data-testid="poll-custom-heading">
                  {T.customHeading}
                </p>
                <p className="text-xs md:text-sm text-neutral-500 mt-0.5">{T.customHint}</p>

                {suggestionSent ? (
                  <p
                    className="mt-3 text-sm text-green-700 flex items-center gap-1.5 font-medium"
                    data-testid="poll-thankyou-suggest"
                  >
                    <Check className="h-4 w-4" /> {T.thankSuggest}
                  </p>
                ) : (
                  <div className="mt-3 relative max-w-2xl">
                    <Input
                      value={suggestion}
                      onChange={(e) => setSuggestion(e.target.value)}
                      placeholder={T.suggestPlaceholder}
                      maxLength={80}
                      className="h-12 pr-11 rounded-full border-sky-200 bg-white text-sm md:text-base placeholder:text-neutral-400 focus-visible:ring-sky-300"
                      data-testid="poll-suggestion-input"
                      onKeyDown={(e) => { if (e.key === 'Enter') submitAll(); }}
                    />
                    <Pencil className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-sky-400" />
                  </div>
                )}
              </div>
            )}

            {/* Submit-Button (nur wenn irgendwas absendbar ist) */}
            {(!ended && (canVote || canSuggest)) && (
              <div className="mt-8 flex flex-col items-center gap-2">
                <Button
                  onClick={submitAll}
                  disabled={submitting || voting || (!canVote && !canSuggest)}
                  className="rounded-full bg-sky-700 hover:bg-sky-800 text-white font-bold px-8 h-12 text-sm md:text-base shadow-md transition-all hover:-translate-y-0.5"
                  data-testid="poll-submit-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {T.submitLabel}
                  {canVote && picks.size > 0 ? ` (${picks.size})` : ''}
                </Button>
                <span className="text-xs md:text-sm text-sky-700 flex items-center gap-1.5" data-testid="poll-footer-thanks">
                  <Heart className="h-3.5 w-3.5" /> {T.footerThanks}
                </span>
              </div>
            )}
          </div>

          {/* Deko */}
          {showBalloon && <Balloon />}
          {showMascot && (
            poll.mascot_url
              ? (
                <img
                  src={poll.mascot_url}
                  alt=""
                  aria-hidden
                  loading="lazy"
                  className="pointer-events-none absolute bottom-0 right-2 md:right-4 w-24 md:w-40 lg:w-48 object-contain"
                />
              )
              : <DefaultMascot />
          )}
        </div>
      </div>
    </section>
  );
};

export default PollSection;

// ============================================================
// Creative Compact Variant — großes Split-Layout mit gekippten
// Foto-Karten aus den Poll-Options (image_url). Fällt auf ein
// stilvolles Placeholder-Design zurück, wenn keine Bilder gesetzt sind.
// ============================================================
const PollCompactCreative = ({ poll, T, options, cdChip }) => {
  // Bilder aus den Poll-Optionen ziehen (max. 4 für die Collage).
  const cardOptions = options.filter((o) => !!o.image_url).slice(0, 4);
  const previewOptions = options.slice(0, 4);
  const totalCount = options.length;

  // Headline aufteilen: das letzte Wort bekommt den gelben Schwung-Unterstrich.
  const headlineParts = (() => {
    const raw = (T.headline || '').trim();
    if (!raw) return { head: '', tail: '' };
    const idx = raw.lastIndexOf(' ');
    if (idx <= 0) return { head: '', tail: raw };
    return { head: raw.slice(0, idx), tail: raw.slice(idx + 1) };
  })();

  // Feste Rotations- und Offset-Werte für die 4 Karten (überlappend gestapelt).
  const cardTransforms = [
    { rotate: -8, translateY: 6, z: 10 },
    { rotate: 4, translateY: -4, z: 20 },
    { rotate: -3, translateY: 8, z: 30 },
    { rotate: 7, translateY: -2, z: 40 },
  ];

  const resolveImg = (url) => (url?.startsWith('/api/') ? `${BACKEND_URL}${url}` : url);

  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setExpanded((v) => !v);
  };

  return (
    <section className="relative py-2 md:py-4 overflow-hidden" data-testid="poll-section-compact">
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/umfrage"
          className="group block relative"
          data-testid="poll-creative-link"
        >
          <div className="relative grid grid-cols-1 md:grid-cols-[1fr_1.3fr] items-center gap-3 md:gap-6">
            {/* LEFT — Text & CTA */}
            <div className="relative min-w-0 space-y-2.5 md:space-y-3">
              {/* Eyebrow */}
              <div className="flex items-center gap-2 text-sky-700 flex-wrap">
                <Vote className="h-4 w-4" />
                <span className="text-[11px] font-black uppercase tracking-[0.22em]">{T.eyebrow}</span>
                {cdChip}
              </div>

              {/* Headline mit gelbem Schwung-Unterstrich auf dem letzten Wort */}
              <h2
                className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-black text-sky-800 leading-[1.05]"
                data-testid="poll-creative-headline"
              >
                {headlineParts.head && <span>{headlineParts.head} </span>}
                <span className="relative inline-block">
                  <span className="relative z-10">{headlineParts.tail}</span>
                  <svg
                    aria-hidden
                    className="absolute left-0 right-0 -bottom-1 w-full h-2.5 md:h-4 text-amber-400"
                    viewBox="0 0 200 14"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M2 10 C 40 2, 80 14, 120 6 S 190 4, 198 8"
                      stroke="currentColor"
                      strokeWidth="4"
                      strokeLinecap="round"
                      fill="none"
                    />
                  </svg>
                </span>
              </h2>

              {/* Untertitel — auf Mobile kompakt, immer sichtbar */}
              <p className="text-sm md:text-xl font-semibold md:font-bold text-sky-900 leading-snug">
                {T.subtitle}
              </p>

              {/* Erweiterte Details — Mobile: nur wenn expanded; Desktop: immer sichtbar */}
              <div className={`${expanded ? 'block' : 'hidden'} md:block space-y-3 md:space-y-4`}>
                {poll.question && (
                  <p className="text-sm md:text-base text-sky-900/70">{poll.question}</p>
                )}

                {/* Info-Chip */}
                <div className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur-sm border border-sky-100 px-3.5 py-1.5 text-[11px] md:text-xs text-sky-800">
                  <svg aria-hidden viewBox="0 0 24 24" className="h-3.5 w-3.5 md:h-4 md:w-4 text-sky-500" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  <span>Mehrfachauswahl möglich · Eigene Ideen willkommen</span>
                </div>

                {/* Option-Chips (Preview) */}
                {previewOptions.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 md:gap-2 pt-1">
                    {previewOptions.map((o) => {
                      const Icon = resolvePollIcon(o.icon, o.label);
                      const img = resolveImg(o.image_url);
                      return (
                        <span
                          key={o.id}
                          className="inline-flex items-center gap-2 rounded-full bg-white border border-sky-100 text-sky-800 pl-1 pr-3 py-1 text-[11px] md:text-xs font-semibold shadow-sm"
                        >
                          {img ? (
                            <img src={img} alt="" className="h-5 w-5 md:h-6 md:w-6 rounded-full object-cover ring-2 ring-white" />
                          ) : (
                            <span className="h-5 w-5 md:h-6 md:w-6 rounded-full bg-sky-100 grid place-items-center">
                              <Icon className="h-3 w-3 md:h-3.5 md:w-3.5 text-sky-600" />
                            </span>
                          )}
                          {o.label}
                        </span>
                      );
                    })}
                    {totalCount > previewOptions.length && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 text-sky-700 px-3 py-1 text-[11px] md:text-xs font-semibold">
                        +{totalCount - previewOptions.length} weitere
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Mobile: "Details" Toggle */}
              <button
                type="button"
                onClick={toggleExpanded}
                className="md:hidden inline-flex items-center gap-1.5 text-xs font-semibold text-sky-700 hover:text-sky-900 transition-colors"
                data-testid="poll-creative-toggle"
                aria-expanded={expanded}
              >
                {expanded ? 'Weniger anzeigen' : 'Details anzeigen'}
                <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
              </button>

              {/* CTA — Desktop */}
              <div className="pt-2 hidden md:block">
                <span className="inline-flex items-center gap-2 rounded-full bg-yellow-400 group-hover:bg-yellow-500 text-sky-900 font-black px-7 py-4 text-base shadow-md transition-colors">
                  Jetzt abstimmen
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </div>

            {/* RIGHT — gekippte Foto-Karten */}
            <div className="relative h-[210px] sm:h-[280px] md:h-[320px] lg:h-[350px]">
              {/* Karten-Collage — auf Desktop rechtsbündig, damit sie nicht über den Text kippen */}
              <div className="absolute inset-0 flex items-center justify-center md:justify-end">
                <div className="flex items-center -space-x-5 sm:-space-x-7 md:-space-x-10 md:pr-2">
                  {(cardOptions.length > 0 ? cardOptions : previewOptions.slice(0, 4)).map((o, idx) => {
                    const t = cardTransforms[idx % cardTransforms.length];
                    const img = resolveImg(o.image_url);
                    return (
                      <div
                        key={o.id}
                        className="relative shrink-0 w-[26vw] max-w-[145px] sm:max-w-none sm:w-[155px] md:w-[160px] lg:w-[180px] aspect-[3/4] rounded-xl md:rounded-2xl bg-white shadow-[0_25px_40px_-15px_rgba(2,132,199,0.4)] ring-1 ring-black/5 overflow-hidden transition-transform duration-500 group-hover:-translate-y-1"
                        style={{
                          transform: `rotate(${t.rotate}deg) translateY(${t.translateY}px)`,
                          zIndex: t.z,
                        }}
                      >
                        <div className="absolute inset-[3px] md:inset-1 rounded-lg md:rounded-xl overflow-hidden bg-neutral-100">
                          {img ? (
                            <img
                              src={img}
                              alt={o.label}
                              loading="lazy"
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-sky-500 bg-sky-50">
                              {(() => {
                                const Icon = resolvePollIcon(o.icon, o.label);
                                return <Icon className="h-7 w-7 md:h-14 md:w-14" />;
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* CTA Mobile */}
            <div className="md:hidden">
              <span className="inline-flex items-center justify-center gap-2 rounded-full bg-yellow-400 group-hover:bg-yellow-500 text-sky-900 font-black px-6 py-3 text-sm shadow-md transition-colors w-full">
                Jetzt abstimmen
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
};
